import { CustomPagination } from 'components';
import { UserContext } from 'context';
import React, { useEffect, useContext, useRef } from 'react';
import { constant } from 'constants/constant';
import './masterDetails.css'
import { AddressBookDetails, BranchDetails, ConsigneeDetails, ConsignorDetails, DriverDetails, FuelStationDetails, StaffDetails, SupplierDetails, UnitDetails, VehicleDetails, VendorsDetails } from 'pages';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import StorageService from 'utils/StorageServices/StorageServices';
import { getConsignorListData } from '_services/accountsServices/consignorServices';
import { getSupplierListData } from '_services/accountsServices/supplierServices';
import { getAddressBookListData } from '_services/accountsServices/addressBookService';
import { getUnitListData } from '_services/accountsServices/unitServices';
import { getConsigneeListData } from '_services/accountsServices/consigneeServices';
import { getOwnDriverListData } from '_services/accountsServices/ownDriverServices';
import { getStaffListData } from '_services/accountsServices/staffServices';
import { getBranchListData } from '_services/accountsServices/branchServices';
import { getFuelStationListData } from '_services/accountsServices/fuelStationServices';
import { getVendersListData } from '_services/accountsServices/vendorsServices';
import { getMarketDriverListData } from '_services/accountsServices/marketDriverServices';
import { getVehicleListData } from '_services/accountsServices/vehicleServices';

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const configConstant = {
    1: {
        route: (id) => `/account/consignor/details/${id}`,
        apiFn: getConsignorListData,
        listKey: "consignor_list",
        extraParams: { status: 1 },
    },
    2: {
        route: (id) => `/account/supplier/details/${id}`,
        apiFn: getSupplierListData,
        listKey: "supplier_list",
        extraParams: { status: 1 },
    },
    3: {
        route: (id) => `/address_book/details/${id}`,
        apiFn: getAddressBookListData,
        listKey: "addressbook_list",
        extraParams: { status: 1 },
    },
    4: {
        route: (id) => `/account/unit/details/${id}`,
        apiFn: getUnitListData,
        listKey: "unit_list",
        extraParams: { status: 1 },
    },
    5: {
        route: (id) => `/account/consignee/details/${id}`,
        apiFn: getConsigneeListData,
        listKey: "consignee_list",
        extraParams: { status: 1 },
    },
    6: {
        route: (id) => `/account/vehicle/details/${id}`,
        apiFn: getVehicleListData,
        listKey: "vehicle_list",
        extraParams: { vehicle_ownership: 0, vehicle_status: 0 },
    },
    7: {
        route: (id) => `/account/vendors/details/${id}`,
        apiFn: getVendersListData,
        listKey: "list",
    },
    8: {
        route: (id) => `/account/fuel_station/details/${id}`,
        apiFn: getFuelStationListData,
        listKey: "list",
    },
    9: {
        route: (id) => `/account/branch/details/${id}`,
        apiFn: getBranchListData,
        listKey: "branch_list",
    },
    10: {
        route: (id) => `/accounting/staff/details/${id}`,
        apiFn: getStaffListData,
        listKey: "list",
    },
    11: {
        route: (id) => `/account/driver/details/${id}`,
        apiFn: getOwnDriverListData,
        listKey: "driver_list",
        extraParams: { status: 1, driver_type: 1 },
    },
    12: {
        route: (id) => `/account/driver/details/${id}`,
        apiFn: getMarketDriverListData,
        listKey: "driver_list",
        extraParams: { status: 1, driver_type: 2 },
    },
};

const MasterDetails = () => {

    const { masterData, masterDetailsList, setMasterDetailsList, setTRipFilter } = useContext(UserContext);
    const location = useLocation();
    const navigate = useNavigate();
    const loadingBarRef = useRef(null);
    let { id } = useParams();
    const query = useQuery();

    const qType = +query.get('type');
    const qTab = query.get('tab') || 1;
    const qSearch = query.get('search') || '';
    const qOffset = +query.get('offset');
    const qTotalRecord = query.get('total_record') || 0;
    const qListApi = query.get('is_list') || false;

    const itemsPerPage = 25;
    const dynamicList = masterDetailsList?.list;
    const dynamicDetails = masterDetailsList?.details;

    const fetchList = async (type, offset = "") => {
        const config = configConstant[type];
        if (!config) return;

        const { apiFn, listKey, extraParams = {} } = config;

        try {
            if (loadingBarRef.current) loadingBarRef.current.continuousStart();

            const object = {
                limit: 25,
                offset: offset ? offset : +qOffset,
                search: qSearch,
                company_id: StorageService.getCompanyId(),
                firm_id: StorageService.getFirmId(),
                ...extraParams,
            };

            const res = await apiFn(object);

            if (res?.responseCode == 200) {
                const list = res?.data?.[listKey];
                const element = list?.find((el) => el?._id === id);
                setMasterDetailsList((s) => ({ ...s, list, details: element }));

                const params = new URLSearchParams(location.search);
                params.set('offset', offset ? offset : +qOffset);
                params.set('total_record', res?.data?.total_record);
                navigate(`${location.pathname}?${params.toString()}`);
            } else {
                console.error('Error');
            }
        } catch (error) {
            console.error('Error', error);
        } finally {
            if (loadingBarRef.current) loadingBarRef.current.complete();
        }
    };

    const handlerFunction = (value, type) => {
        const config = configConstant[type];
        if (!config) return;

        const params = new URLSearchParams(location.search);
        if (type === 6 && value.vehicle_ownership === 2) {
            params.set('tab', +1);
        } else {
            params.set('tab', qTab);
        }
        params.set('name', value?.name ? value?.name : '');
        navigate(`${config.route(value?._id)}?${params.toString()}`);

        if (type === 2) {
            setTRipFilter((pre) => ({
                ...pre,
                supplier_id: value?._id || '',
                supplier_name: value?.name || '',
            }));
        } else {
            setTRipFilter((pre) => ({
                ...pre,
                supplier_id: '',
                supplier_name: '',
            }));
        }
        setMasterDetailsList((s) => ({ ...s, details: value }));
    };

    useEffect(() => {
        if (masterDetailsList?.list?.length === 0) {
            fetchList(qType);
        }
    }, []);

    useEffect(() => {
        if (qListApi) {
            fetchList(qType);
        }
    }, [qType, qListApi]);

    const handlePageChange = (page) => fetchList(qType, page);

    const getItemLabel = (item) => {
        if (qType === 6) return item?.vehicle_no;
        if (qType === 9) return item?.branch;
        return item?.name || 'Not available';
    };

    return (
        <>
            <div className='__cmnListDetail'>
                <div className='__cmnListBx_ns'>
                    <div className='__cmnListBx_ns_head'> All {constant.typeName[qType]} </div>

                    <div className="__cmnListBx_ns_content">
                        <div className='__cmnListBx_ns_content_list'>
                            {dynamicList?.map((item) => (
                                <React.Fragment key={item?._id}>
                                    <div
                                        className={`__cmnInLine ${item?._id === id ? "active" : ""}`}
                                        onClick={() => handlerFunction(item, qType)}
                                    >
                                        <div className={qType === 6 ? 'flexMaster_details fl_details_vehicle' : 'flexMaster_details'}>
                                            <span>{getItemLabel(item)}</span>
                                            {qType === 6 && <span
                                                className={
                                                    item?.vehicle_ownership == 1
                                                        ? "_trkMark _lrDelivered_status"
                                                        : item?.vehicle_ownership == 2
                                                            ? "_trkMark _lrPartyPoint_status"
                                                            : "_trkMark _lrInTransit_status"
                                                }
                                            >
                                                {masterData?.vehicle_ownership[item?.vehicle_ownership]}
                                            </span>}
                                            {![3, 4, 5, 6, 9, 12].includes(qType) && (
                                                <span style={{ color: item.balance < 0 ? '#dc3545' : item.balance > 0 ? '#28a745' : '#1450ec' }}>
                                                    &#x20B9; {Math.abs(item.balance).toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>

                        {dynamicList?.length > 0 && (
                            <div className='paginationFt_avt'>
                                <CustomPagination
                                    totalItems={qTotalRecord}
                                    itemsPerPage={itemsPerPage}
                                    currentPage={qOffset}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="__cmnDetailBx">
                    {qType === 1 && <ConsignorDetails />}
                    {qType === 2 && <SupplierDetails />}
                    {qType === 3 && <AddressBookDetails />}
                    {qType === 4 && <UnitDetails />}
                    {qType === 5 && <ConsigneeDetails />}
                    {qType === 6 && <VehicleDetails />}
                    {qType === 7 && <VendorsDetails />}
                    {qType === 8 && <FuelStationDetails />}
                    {qType === 9 && <BranchDetails />}
                    {qType === 10 && <StaffDetails />}
                    {(qType === 11 || qType === 12) && <DriverDetails />}
                </div>
            </div>
        </>
    );
};

export default MasterDetails;

// *********  Note : This commented code is working fine **********

// import { CommonToast, CustomPagination } from 'components';
// import { UserContext } from 'context';
// import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
// import { constant } from 'constants/constant';
// import './masterDetails.css'
// import { AddressBookDetails, BranchDetails, ConsigneeDetails, ConsignorDetails, DriverDetails, FuelStationDetails, StaffDetails, SupplierDetails, UnitDetails, VehicleDetails, VendorsDetails } from 'pages';
// import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
// import StorageService from 'utils/StorageServices/StorageServices';
// import { getConsignorListData } from '_services/accountsServices/consignorServices';
// import { getSupplierListData } from '_services/accountsServices/supplierServices';
// import toast from 'react-hot-toast';
// import { getAddressBookListData } from '_services/accountsServices/addressBookService';
// import { getUnitListData } from '_services/accountsServices/unitServices';
// import { getConsigneeListData } from '_services/accountsServices/consigneeServices';
// import { getOwnDriverListData } from '_services/accountsServices/ownDriverServices';
// import { getStaffListData } from '_services/accountsServices/staffServices';
// import { getBranchListData } from '_services/accountsServices/branchServices';
// import { getFuelStationListData } from '_services/accountsServices/fuelStationServices';
// import { getVendersListData } from '_services/accountsServices/vendorsServices';
// import { getMarketDriverListData } from '_services/accountsServices/marketDriverServices';
// import { getVehicleListData } from '_services/accountsServices/vehicleServices';

// const useQuery = () => {
//     return new URLSearchParams(useLocation().search);
// };

// const MasterDetails = () => {

//     const { mastersChild, masterDetailsList, setMasterDetailsList, setTRipFilter } = useContext(UserContext);
//     const location = useLocation();
//     const navigate = useNavigate();
//     const loadingBarRef = useRef(null);
//     let { id } = useParams();
//     const query = useQuery();

//     const qType = +query.get('type');
//     const qTab = query.get('tab') || 1;
//     const qSearch = query.get('search') || '';
//     const qOffset = +query.get('offset');
//     const qTotalRecord = query.get('total_record') || 0;

//     const itemsPerPage = 25;
//     const dynamicList = masterDetailsList?.list;
//     const dynamicDetails = masterDetailsList?.details;

//     const handlerFunction = (value, type) => {
//         if (type === 1) {
//             const params = new URLSearchParams(location.search);
//             params.set('tab', qTab);
//             navigate(`/account/consignor/details/${value?._id}?${params.toString()}`);
//         }
//         if (type === 2) {
//             const params = new URLSearchParams(location.search);
//             params.set('tab', qTab);
//             navigate(`/account/supplier/details/${value?._id}?${params.toString()}`);

//             setTRipFilter((pre) => ({
//                 ...pre,
//                 supplier_id: value?._id || '',
//                 supplier_name: value?.name || ''
//             }))
//         }
//         if (type === 3) {
//             const params = new URLSearchParams(location.search);
//             params.set('tab', qTab);
//             navigate(`/address_book/details/${value?._id}?${params.toString()}`);
//         }
//         if (type === 4) {
//             const params = new URLSearchParams(location.search);
//             params.set('tab', qTab);
//             navigate(`/account/unit/details/${value?._id}?${params.toString()}`);
//         }
//         if (type === 5) {
//             const params = new URLSearchParams(location.search);
//             params.set('tab', qTab);
//             navigate(`/account/consignee/details/${value?._id}?${params.toString()}`);
//         }
//         if (type === 6) {
//             const params = new URLSearchParams(location.search);
//             params.set('tab', qTab);
//             navigate(`/account/vehicle/details/${value?._id}?${params.toString()}`);
//         }
//         if (type === 7) {
//             const params = new URLSearchParams(location.search);
//             params.set('tab', qTab);
//             navigate(`/account/vendors/details/${value?._id}?${params.toString()}`);
//         }
//         if (type === 8) {
//             const params = new URLSearchParams(location.search);
//             params.set('tab', qTab);
//             navigate(`/account/fuel_station/details/${value?._id}?${params.toString()}`);
//         }
//         if (type === 9) {
//             const params = new URLSearchParams(location.search);
//             params.set('tab', qTab);
//             navigate(`/account/branch/details/${value?._id}?${params.toString()}`);
//         }
//         if (type === 10) {
//             const params = new URLSearchParams(location.search);
//             params.set('tab', qTab);
//             navigate(`/accounting/staff/details/${value?._id}?${params.toString()}`);
//         }
//         if (type === 11 || type === 12) {
//             const params = new URLSearchParams(location.search);
//             params.set('tab', qTab);
//             navigate(`/account/driver/details/${value?._id}?${params.toString()}`);
//         }
//         setMasterDetailsList((s) => ({ ...s, details: value }));
//         setTRipFilter((pre) => ({
//             ...pre,
//             supplier_id: '',
//             supplier_name: ''
//         }))
//     }

//     const getDataConsignorList = async (offset = "") => {
//         try {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.continuousStart(); // Start the loading bar
//             }
//             let object = {
//                 limit: 25,
//                 offset: offset ? offset : +qOffset,
//                 search: qSearch,
//                 status: 1,
//                 company_id: StorageService.getCompanyId(),
//                 firm_id: StorageService.getFirmId(),
//             }
//             const res = await getConsignorListData(object);
//             if (res?.responseCode == 200) {
//                 let element = res?.data?.consignor_list?.find((el) => el?._id === id);
//                 setMasterDetailsList((s) => ({ ...s, list: res?.data?.consignor_list, details: element }));

//                 const params = new URLSearchParams(location.search);
//                 params.set('offset', offset ? offset : +qOffset);
//                 params.set('total_record', res?.data?.total_record);
//                 navigate(`${location.pathname}?${params.toString()}`);
//             }
//         } catch (error) {
//             // throw error;
//         } finally {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.complete(); // Complete the loading bar
//             }
//         }
//     }

//     const getDataSupplierList = async (offset = "") => {
//         try {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.continuousStart(); // Start the loading bar
//             }
//             let object = {
//                 limit: 25,
//                 offset: offset ? offset : +qOffset,
//                 search: qSearch,
//                 status: 1,
//                 company_id: StorageService.getCompanyId(),
//                 firm_id: StorageService.getFirmId(),
//             };
//             const res = await getSupplierListData(object);
//             if (res?.responseCode === 200) {
//                 let element = res?.data?.supplier_list?.find((el) => el?._id === id);
//                 setMasterDetailsList((s) => ({ ...s, list: res?.data?.supplier_list, details: element }));

//                 const params = new URLSearchParams(location.search);
//                 params.set('offset', offset ? offset : +qOffset);
//                 params.set('total_record', res?.data?.total_record);
//                 navigate(`${location.pathname}?${params.toString()}`);
//             }
//         } catch (error) {
//             toast.error(error)
//         } finally {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.complete();
//             }
//         }
//     };

//     const getAddressBookList = async (offset = "") => {
//         try {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.continuousStart(); // Start the loading bar
//             }
//             let object = {
//                 limit: 25,
//                 offset: offset ? offset : +qOffset,
//                 search: qSearch,
//                 status: 1,
//                 company_id: StorageService.getCompanyId(),
//                 firm_id: StorageService.getFirmId(),
//             };
//             const res = await getAddressBookListData(object);
//             if (res?.responseCode === 200) {

//                 let element = res.data?.addressbook_list?.find((el) => el?._id === id);
//                 setMasterDetailsList((s) => ({ ...s, list: res.data?.addressbook_list, details: element }));

//                 const params = new URLSearchParams(location.search);
//                 params.set('offset', offset ? offset : +qOffset);
//                 params.set('total_record', res?.data?.total_record);
//                 navigate(`${location.pathname}?${params.toString()}`);
//             }
//         } catch (error) {
//             toast.error(error);
//         } finally {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.complete();
//             }
//         }
//     };

//     const getDataUnitList = async (offset = "") => {
//         try {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.continuousStart(); // Start the loading bar
//             }
//             let object = {
//                 limit: 25,
//                 offset: offset ? offset : +qOffset,
//                 search: qSearch,
//                 status: 1,
//                 company_id: StorageService.getCompanyId(),
//                 firm_id: StorageService.getFirmId(),
//             }
//             const res = await getUnitListData(object);
//             if (res?.responseCode == 200) {

//                 let element = res?.data?.unit_list?.find((el) => el?._id === id);
//                 setMasterDetailsList((s) => ({ ...s, list: res?.data?.unit_list, details: element }));

//                 const params = new URLSearchParams(location.search);
//                 params.set('offset', offset ? offset : +qOffset);
//                 params.set('total_record', res?.data?.total_record);
//                 navigate(`${location.pathname}?${params.toString()}`);
//             }
//         } catch (error) {
//             // throw error;
//         } finally {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.complete(); // Complete the loading bar
//             }
//         }
//     }

//     const getDataConsigneeList = async (offset = "") => {
//         try {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.continuousStart();
//             }

//             let object = {
//                 limit: 25,
//                 offset: offset ? offset : +qOffset,
//                 search: qSearch,
//                 status: 1,
//                 company_id: StorageService.getCompanyId(),
//                 firm_id: StorageService.getFirmId(),
//             };
//             const res = await getConsigneeListData(object);
//             if (res?.responseCode == 200) {

//                 let element = res?.data?.consignee_list?.find((el) => el?._id === id);
//                 setMasterDetailsList((s) => ({ ...s, list: res?.data?.consignee_list, details: element }));

//                 const params = new URLSearchParams(location.search);
//                 params.set('offset', offset ? offset : +qOffset);
//                 params.set('total_record', res?.data?.total_record);
//                 navigate(`${location.pathname}?${params.toString()}`);
//             }
//         } catch (error) {
//             // throw error;
//         } finally {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.complete(); // Complete the loading bar
//             }
//         }
//     };

//     const getDataVehicleList = async (offset = "") => {
//         try {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.continuousStart(); // Start the loading bar
//             }
//             let object = {
//                 limit: 25,
//                 offset: offset ? offset : +qOffset,
//                 search: qSearch,
//                 vehicle_ownership: 0,
//                 vehicle_status: 0,
//                 company_id: StorageService.getCompanyId(),
//                 firm_id: StorageService.getFirmId(),
//             }
//             const res = await getVehicleListData(object);
//             if (res?.responseCode == 200) {
//                 let element = res?.data?.vehicle_list?.find((el) => el?._id === id);
//                 setMasterDetailsList((s) => ({ ...s, list: res?.data?.vehicle_list, details: element }));

//                 const params = new URLSearchParams(location.search);
//                 params.set('offset', offset ? offset : +qOffset);
//                 params.set('total_record', res?.data?.total_record);
//                 navigate(`${location.pathname}?${params.toString()}`);
//             }
//         } catch (error) {
//             console.error('Error', error)
//         } finally {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.complete(); // Complete the loading bar
//             }
//         }
//     }

//     const getDataOwnDriverList = async (offset = "") => {
//         try {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.continuousStart(); // Start the loading bar
//             }
//             let object = {
//                 limit: 25,
//                 offset: offset ? offset : +qOffset,
//                 search: qSearch,
//                 status: 1,
//                 driver_type: 1,
//                 company_id: StorageService.getCompanyId(),
//                 firm_id: StorageService.getFirmId()
//             }
//             const res = await getOwnDriverListData(object);
//             if (res?.responseCode == 200) {

//                 let element = res?.data?.driver_list?.find((el) => el?._id === id);
//                 setMasterDetailsList((s) => ({ ...s, list: res?.data?.driver_list, details: element }));

//                 const params = new URLSearchParams(location.search);
//                 params.set('offset', offset ? offset : +qOffset);
//                 params.set('total_record', res?.data?.total_record);
//                 navigate(`${location.pathname}?${params.toString()}`);
//             }
//         } catch (error) {
//             // throw error;
//         } finally {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.complete(); // Complete the loading bar
//             }
//         }
//     }

//     const getDataMarketDriverList = async (offset = "") => {
//         try {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.continuousStart(); // Start the loading bar
//             }
//             let object = {
//                 limit: 25,
//                 offset: offset ? offset : +qOffset,
//                 search: qSearch,
//                 status: 1,
//                 driver_type: 2,
//                 company_id: StorageService.getCompanyId(),
//                 firm_id: StorageService.getFirmId()
//             }
//             const res = await getMarketDriverListData(object);
//             if (res?.responseCode == 200) {
//                 let element = res?.data?.driver_list?.find((el) => el?._id === id);
//                 setMasterDetailsList((s) => ({ ...s, list: res?.data?.driver_list, details: element }));

//                 const params = new URLSearchParams(location.search);
//                 params.set('offset', offset ? offset : +qOffset);
//                 params.set('total_record', res?.data?.total_record);
//                 navigate(`${location.pathname}?${params.toString()}`);
//             }
//         } catch (error) {
//             // throw error;
//         } finally {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.complete(); // Complete the loading bar
//             }
//         }
//     }

//     const getDataVendersList = async (offset = "") => {
//         try {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.continuousStart(); // Start the loading bar
//             }
//             let object = {
//                 limit: 25,
//                 offset: offset ? offset : +qOffset,
//                 search: qSearch,
//                 company_id: StorageService.getCompanyId(),
//                 firm_id: StorageService.getFirmId()
//             }
//             const res = await getVendersListData(object);
//             if (res?.responseCode == 200) {
//                 let element = res?.data?.list?.find((el) => el?._id === id);
//                 setMasterDetailsList((s) => ({ ...s, list: res?.data?.list, details: element }));

//                 const params = new URLSearchParams(location.search);
//                 params.set('offset', offset ? offset : +qOffset);
//                 params.set('total_record', res?.data?.total_record);
//                 navigate(`${location.pathname}?${params.toString()}`);
//             }
//         } catch (error) {
//             // throw error;
//         } finally {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.complete(); // Complete the loading bar
//             }
//         }
//     }

//     const getDataFuelStationList = async (offset = "") => {
//         try {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.continuousStart(); // Start the loading bar
//             }
//             let object = {
//                 limit: 25,
//                 offset: offset ? offset : +qOffset,
//                 search: qSearch,
//                 company_id: StorageService.getCompanyId(),
//                 firm_id: StorageService.getFirmId()
//             }
//             const res = await getFuelStationListData(object);
//             if (res?.responseCode == 200) {
//                 let element = res?.data?.list?.find((el) => el?._id === id);
//                 setMasterDetailsList((s) => ({ ...s, list: res?.data?.list, details: element }));

//                 const params = new URLSearchParams(location.search);
//                 params.set('offset', offset ? offset : +qOffset);
//                 params.set('total_record', res?.data?.total_record);
//                 navigate(`${location.pathname}?${params.toString()}`);
//             }
//         } catch (error) {
//             // throw error;
//         } finally {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.complete(); // Complete the loading bar
//             }
//         }
//     }

//     const getDatabranchList = async (offset = "") => {
//         try {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.continuousStart(); // Start the loading bar
//             }
//             let object = {
//                 limit: 25,
//                 offset: offset ? offset : +qOffset,
//                 search: qSearch,
//                 company_id: StorageService.getCompanyId(),
//                 firm_id: StorageService.getFirmId()
//             }
//             const res = await getBranchListData(object);
//             if (res?.responseCode == 200) {

//                 let element = res?.data?.branch_list?.find((el) => el?._id === id);
//                 setMasterDetailsList((s) => ({ ...s, list: res?.data?.branch_list, details: element }));

//                 const params = new URLSearchParams(location.search);
//                 params.set('offset', offset ? offset : +qOffset);
//                 params.set('total_record', res?.data?.total_record);
//                 navigate(`${location.pathname}?${params.toString()}`);
//             } else {
//                 CommonToast(res?.responseMessage)
//             }
//         } catch (error) {
//             toast.error(error.message)
//         } finally {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.complete(); // Complete the loading bar
//             }
//         }
//     }

//     const getDataStaffList = async (offset = "") => {
//         try {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.continuousStart(); // Start the loading bar
//             }
//             let object = {
//                 limit: 25,
//                 offset: offset ? offset : +qOffset,
//                 search: qSearch,
//                 company_id: StorageService.getCompanyId(),
//                 firm_id: StorageService.getFirmId()
//             }
//             const res = await getStaffListData(object);
//             if (res?.responseCode == 200) {
//                 let element = res?.data?.list?.find((el) => el?._id === id);
//                 setMasterDetailsList((s) => ({ ...s, list: res?.data?.list, details: element }));

//                 const params = new URLSearchParams(location.search);
//                 params.set('offset', offset ? offset : +qOffset);
//                 params.set('total_record', res?.data?.total_record);
//                 navigate(`${location.pathname}?${params.toString()}`);
//             }
//         } catch (error) {
//             toast.error(`Error`);
//         } finally {
//             if (loadingBarRef.current) {
//                 loadingBarRef.current.complete(); // Complete the loading bar
//             }
//         }
//     }

//     useEffect(() => {
//         if (masterDetailsList?.list?.length === 0 && qType === 1) {
//             getDataConsignorList();
//         }
//         if (masterDetailsList?.list?.length === 0 && qType === 2) {
//             getDataSupplierList();
//         }
//         if (masterDetailsList?.list?.length === 0 && qType === 3) {
//             getAddressBookList();
//         }
//         if (masterDetailsList?.list?.length === 0 && qType === 4) {
//             getDataUnitList();
//         }
//         if (masterDetailsList?.list?.length === 0 && qType === 5) {
//             getDataConsigneeList();
//         }
//         if (masterDetailsList?.list?.length === 0 && qType === 6) {
//             getDataVehicleList();
//         }
//         if (masterDetailsList?.list?.length === 0 && qType === 7) {
//             getDataVendersList();
//         }
//         if (masterDetailsList?.list?.length === 0 && qType === 8) {
//             getDataFuelStationList();
//         }
//         if (masterDetailsList?.list?.length === 0 && qType === 9) {
//             getDatabranchList();
//         }
//         if (masterDetailsList?.list?.length === 0 && qType === 10) {
//             getDataStaffList();
//         }
//         if (masterDetailsList?.list?.length === 0 && qType === 11) {
//             getDataOwnDriverList();
//         }
//         if (masterDetailsList?.list?.length === 0 && qType === 12) {
//             getDataMarketDriverList();
//         }
//     }, [])

//     const handlePageChange = (page) => {
//         if (qType === 1) {
//             getDataConsignorList(page);
//         }
//         if (qType === 2) {
//             getDataSupplierList(page);
//         }
//         if (qType === 3) {
//             getAddressBookList(page);
//         }
//         if (qType === 4) {
//             getDataUnitList(page);
//         }
//         if (qType === 5) {
//             getDataConsigneeList(page);
//         }
//         if (qType === 6) {
//             getDataVehicleList(page);
//         }
//         if (qType === 7) {
//             getDataVendersList(page);
//         }
//         if (qType === 8) {
//             getDataFuelStationList(page);
//         }
//         if (qType === 9) {
//             getDatabranchList(page);
//         }
//         if (qType === 10) {
//             getDataStaffList(page);
//         }
//         if (qType === 11) {
//             getDataOwnDriverList(page);
//         }
//         if (qType === 12) {
//             getDataMarketDriverList(page);
//         }
//     };

//     return (
//         <>
//             <div className='__cmnListDetail'>
//                 <div className='__cmnListBx_ns'>
//                     <div className='__cmnListBx_ns_head'> All {constant.typeName[qType]} </div>

//                     <div className="__cmnListBx_ns_content">
//                         <div className='__cmnListBx_ns_content_list'>
//                             {dynamicList?.map((item, index) => {
//                                 return (
//                                     <React.Fragment key={item?._id}>
//                                         <div
//                                             key={item?._id}
//                                             className={`__cmnInLine ${item?._id === id ? "active" : ""}`}
//                                             onClick={() => handlerFunction(item, qType)}
//                                         >
//                                             <div className='flexMaster_details'>
//                                                 <span> {qType === 6 ? item?.vehicle_no : (qType === 9 ? item?.branch : (item?.name ? item?.name : 'Not available'))} </span>
//                                                 {(qType !== 3 && qType !== 4 && qType !== 5 && qType !== 6 && qType !== 9 && qType !== 12) && <span style={{ color: item.balance < 0 ? '#dc3545' : item.balance > 0 ? '#28a745' : '#1450ec' }}>
//                                                     &#x20B9; {Math.abs(item.balance).toFixed(2)}
//                                                 </span>}
//                                             </div>
//                                         </div>
//                                     </React.Fragment>
//                                 )
//                             })}
//                         </div>
//                         {dynamicList?.length > 0 && <div className='paginationFt_avt'>
//                             <CustomPagination
//                                 totalItems={qTotalRecord}
//                                 itemsPerPage={itemsPerPage}
//                                 currentPage={qOffset}
//                                 onPageChange={handlePageChange}
//                             />
//                         </div>}
//                     </div>
//                 </div>

// <div className="__cmnDetailBx">
//     {qType === 1 && <ConsignorDetails />}
//     {qType === 2 && <SupplierDetails />}
//     {qType === 3 && <AddressBookDetails />}
//     {qType === 4 && <UnitDetails />}
//     {qType === 5 && <ConsigneeDetails />}
//     {qType === 6 && <VehicleDetails />}
//     {qType === 7 && <VendorsDetails />}
//     {qType === 8 && <FuelStationDetails />}
//     {qType === 9 && <BranchDetails />}
//     {qType === 10 && <StaffDetails />}
//     {(qType === 11 || qType === 12) && <DriverDetails />}
// </div>
//             </div>
//         </>
//     );
// };

// export default MasterDetails;